import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CARE_PLAN_DIALOG_OPTIONS } from '../care-plan-dialog.options';
import { caseApiErrorMessage } from '../cases/api/case-api-error';
import { OutcomeCard } from './api/outcome-api.model';
import {
  OutcomeEditorDialogComponent,
  OutcomeEditorDialogData,
} from './outcome-editor-dialog/outcome-editor-dialog.component';
import { OutcomesStore } from './outcomes.store';

/** One opening path for case pills and client outcome cards. Scoped to its host. */
@Injectable()
export class OutcomeModalService {
  private readonly dialog = inject(DialogService);
  private readonly destroyRef = inject(DestroyRef);
  private ref: DynamicDialogRef | null = null;
  private generation = 0;
  private restoreOptions: (() => void) | null = null;
  readonly opening = signal(false);
  readonly error = signal<string | null>(null);

  constructor() {
    this.destroyRef.onDestroy(() => this.reset());
  }

  reset(): void {
    ++this.generation;
    this.restoreCaseOptions();
    this.ref?.destroy();
    this.ref = null;
    this.opening.set(false);
    this.error.set(null);
  }

  private restoreCaseOptions(): void {
    const restore = this.restoreOptions;
    this.restoreOptions = null;
    restore?.();
  }

  async open(store: OutcomesStore, outcome?: OutcomeCard): Promise<void> {
    if (
      this.opening() ||
      this.ref ||
      store.loading() ||
      store.error() ||
      !store.session()
    )
      return;
    if (
      !outcome &&
      (!store.canWrite() ||
        store.clientScoped() ||
        store.referenceLoading() ||
        store.referenceError() ||
        !store.types().length)
    )
      return;
    const generation = this.generation;
    const session = store.session();
    this.opening.set(true);
    this.error.set(null);
    try {
      const freshOutcome = outcome
        ? await store.getForView(outcome.outcomeId)
        : undefined;
      if (generation !== this.generation || store.session() !== session) return;
      this.ref = this.dialog.open(OutcomeEditorDialogComponent, {
        ...CARE_PLAN_DIALOG_OPTIONS,
        width: '42rem',
        breakpoints: { '768px': 'calc(100vw - 2rem)' },
        styleClass: 'bn-outcome-editor-dialog-host',
        ariaLabelledBy: 'outcome-editor-title',
        data: {
          store,
          outcome: freshOutcome,
        } satisfies OutcomeEditorDialogData,
      });
      const openedRef = this.ref;
      this.restoreOptions = () => {
        if (store.session() !== session) return;
        store.resetCaseOptions();
        if (session && session.caseId !== null) void store.loadReferences();
      };
      this.ref?.onClose
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (this.ref !== openedRef) return;
          this.ref = null;
          this.restoreCaseOptions();
        });
    } catch (error) {
      if (generation === this.generation)
        this.error.set(
          caseApiErrorMessage(
            error,
            'The outcome could not be opened. Please refresh and try again.',
          ),
        );
    } finally {
      if (generation === this.generation) this.opening.set(false);
    }
  }
}
